// Neo's alarm writes (ADR 0210, ADR 0455) — the pure half: decoding the two SSE records,
// the chat chip's wording, and the confirmation card's result and error text.
// The card itself (components/NeoAlarmChangeCard.tsx) holds state + fetch.
//
//   alarm_changes          { changes }   — writes Neo MADE to ordinary alarms:
//                                          created, changed, removed,
//                                          acknowledged or silenced.
//   alarm_change_requests  { requests }  — LIFE-SAFETY writes waiting for the
//                                          owner's click; nothing has changed.

export type AlarmChangeAction =
  | 'create'
  | 'update'
  | 'disable'
  | 'enable'
  | 'delete'
  | 'acknowledge'
  | 'silence';
export type AlarmChangeOutcome = 'applied' | 'removed' | 'disabled_pending_removal';

export interface AlarmChangeDisplay {
  title: string;
  action: AlarmChangeAction;
  lines: string[];
}

/** A change Neo made. Reported whatever the model's reply says. */
export interface NeoAlarmChange {
  boatId: string;
  alarmId: string;
  display: AlarmChangeDisplay;
  outcome: AlarmChangeOutcome;
  delivery: string;
}

/** A life-safety change waiting for the owner's click. */
export interface NeoAlarmChangeRequest {
  boatId: string;
  alarmId: string;
  action: AlarmChangeAction;
  patch?: Record<string, unknown>;
  expectedUpdatedAt: string;
  display: AlarmChangeDisplay;
  lifeSafety: string;
  /** Minted here: the card's idempotency key, so a double click applies once. */
  clientId: string;
}

/** What `POST /v1/neo/alarm-changes/confirm` answers. */
export interface AlarmChangeConfirmResponse {
  outcome: AlarmChangeOutcome | 'no_change';
  delivery?: string;
  note?: string;
}

const ACTIONS: ReadonlySet<string> = new Set([
  'create',
  'update',
  'disable',
  'enable',
  'delete',
  'acknowledge',
  'silence',
]);
/** A life-safety create is the proposal card, never a change request. */
const REQUEST_ACTIONS: ReadonlySet<string> = new Set([
  'update',
  'disable',
  'enable',
  'delete',
  'acknowledge',
  'silence',
]);
const OUTCOMES: ReadonlySet<string> = new Set(['applied', 'removed', 'disabled_pending_removal']);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function stringOf(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function displayOf(raw: unknown): AlarmChangeDisplay | null {
  if (!isRecord(raw)) return null;
  const title = stringOf(raw.title);
  const action = stringOf(raw.action);
  if (title === undefined || action === undefined || !ACTIONS.has(action)) return null;
  if (!Array.isArray(raw.lines)) return null;
  const lines = raw.lines.filter((l): l is string => typeof l === 'string' && l.length > 0);
  // A record that cannot say what changed is not shown.
  if (lines.length === 0) return null;
  return { title, action: action as AlarmChangeAction, lines };
}

function listOf(payload: unknown, key: string): unknown[] {
  const list = isRecord(payload) ? payload[key] : payload;
  return Array.isArray(list) ? list : [];
}

/**
 * Decode an `alarm_changes` event (or the blocking route's `alarmChanges`).
 * `expectedBoatId`, on a boat-scoped surface, drops a record for another boat.
 */
export function parseAlarmChanges(payload: unknown, expectedBoatId?: string): NeoAlarmChange[] {
  const out: NeoAlarmChange[] = [];
  for (const raw of listOf(payload, 'changes')) {
    if (!isRecord(raw)) continue;
    const boatId = stringOf(raw.boatId);
    const alarmId = stringOf(raw.alarmId);
    const outcome = stringOf(raw.outcome);
    const display = displayOf(raw.display);
    if (boatId === undefined || alarmId === undefined || display === null) continue;
    if (outcome === undefined || !OUTCOMES.has(outcome)) continue;
    if (expectedBoatId !== undefined && boatId !== expectedBoatId) continue;
    out.push({
      boatId,
      alarmId,
      display,
      outcome: outcome as AlarmChangeOutcome,
      delivery: stringOf(raw.delivery) ?? 'unchanged',
    });
  }
  return out;
}

/**
 * Decode an `alarm_change_requests` event. A card the owner confirms must be
 * able to say exactly what it does and why it asks, so anything short of that
 * is dropped rather than shown.
 */
export function parseAlarmChangeRequests(
  payload: unknown,
  expectedBoatId?: string
): NeoAlarmChangeRequest[] {
  const out: NeoAlarmChangeRequest[] = [];
  for (const raw of listOf(payload, 'requests')) {
    if (!isRecord(raw)) continue;
    const boatId = stringOf(raw.boatId);
    const alarmId = stringOf(raw.alarmId);
    const action = stringOf(raw.action);
    const expectedUpdatedAt = stringOf(raw.expectedUpdatedAt);
    const lifeSafety = stringOf(raw.lifeSafety);
    const display = displayOf(raw.display);
    if (
      boatId === undefined ||
      alarmId === undefined ||
      action === undefined ||
      !REQUEST_ACTIONS.has(action) ||
      expectedUpdatedAt === undefined ||
      lifeSafety === undefined ||
      display === null
    ) {
      continue;
    }
    if (expectedBoatId !== undefined && boatId !== expectedBoatId) continue;
    out.push({
      boatId,
      alarmId,
      action: action as AlarmChangeAction,
      ...(isRecord(raw.patch) ? { patch: raw.patch } : {}),
      expectedUpdatedAt,
      display,
      lifeSafety,
      clientId: crypto.randomUUID(),
    });
  }
  return out;
}

/** "reached your boat" and friends — what the delivery state means to an owner. */
export function deliveryPhrase(outcome: AlarmChangeOutcome, delivery: string): string {
  if (outcome === 'removed') return 'your boat has stopped watching it';
  if (outcome === 'disabled_pending_removal') {
    // Nothing removes it later on its own (ADR 0213), so never "until your boat
    // takes it" — the same outcome the Alarms page words with `describeRemoval`.
    return delivery === 'skipped'
      ? 'not removed, and this boat isn’t receiving alarm changes, so it’s still running it'
      : 'not removed, as your boat hasn’t taken it yet; remove it again once it’s online';
  }
  switch (delivery) {
    case 'delivered':
      return 'your boat has it';
    case 'staged':
    case 'not_delivered':
      return 'your boat gets it when it next connects';
    case 'skipped':
      return 'alarm delivery to this boat is off';
    default:
      return '';
  }
}

function actionVerb(action: AlarmChangeAction, outcome: AlarmChangeOutcome): string {
  if (action === 'delete') return outcome === 'removed' ? 'Alarm removed' : 'Alarm turned off';
  if (action === 'disable') return 'Alarm turned off';
  if (action === 'enable') return 'Alarm turned on';
  if (action === 'create') return 'Alarm created';
  if (action === 'acknowledge') return 'Alarm acknowledged';
  if (action === 'silence') return 'Alarm silenced';
  return 'Alarm changed';
}

/** Whether the chip spells out the record's lines: an edit or a new alarm. */
function showsLines(action: AlarmChangeAction): boolean {
  return action === 'update' || action === 'create';
}

/** The chat chip for one change Neo made: "✓ Alarm turned off: Exhaust elbow hot — …". */
export function alarmChangeChip(change: NeoAlarmChange): string {
  const head = `✓ ${actionVerb(change.display.action, change.outcome)}: ${change.display.title}`;
  const detail = showsLines(change.display.action) ? ` (${change.display.lines.join('; ')})` : '';
  const tail = deliveryPhrase(change.outcome, change.delivery);
  return tail.length > 0 ? `${head}${detail} — ${tail}` : `${head}${detail}`;
}

/** The card's heading before the click, by action. */
export function changeRequestHeading(action: AlarmChangeAction): string {
  switch (action) {
    case 'delete':
      return 'Remove this alarm?';
    case 'disable':
      return 'Turn this alarm off?';
    case 'enable':
      return 'Turn this alarm back on?';
    case 'update':
      return 'Change this alarm?';
    case 'create':
      return 'Create this alarm?';
    case 'acknowledge':
      return 'Acknowledge this alarm?';
    case 'silence':
      return 'Silence this alarm?';
  }
}

/** The card's eyebrow once the owner has confirmed, by action. */
export function changeRequestDoneLabel(action: AlarmChangeAction): string {
  if (action === 'acknowledge') return 'Alarm acknowledged';
  if (action === 'silence') return 'Alarm silenced';
  return 'Alarm changed';
}

/** The confirm button's label, by action. */
export function changeRequestButton(action: AlarmChangeAction): string {
  switch (action) {
    case 'delete':
      return 'Remove alarm';
    case 'disable':
      return 'Turn off';
    case 'enable':
      return 'Turn on';
    case 'update':
      return 'Apply change';
    case 'create':
      return 'Create alarm';
    case 'acknowledge':
      return 'Acknowledge';
    case 'silence':
      return 'Silence';
  }
}

/** A failed confirm, in plain English. */
export function changeConfirmErrorMessage(err: unknown): string {
  const status = isRecord(err) && typeof err.status === 'number' ? err.status : undefined;
  const code = isRecord(err) && isRecord(err.body) ? stringOf(err.body.error) : undefined;
  if (code === 'boat_offline') {
    return 'Your boat is offline, so it could not be told. Use the alarm panel on board.';
  }
  if (code === 'not_sounding') return 'That alarm is no longer sounding.';
  if (status === 409) return 'This alarm changed after Neo made the card. Ask Neo again.';
  if (status === 404) return 'That alarm no longer exists.';
  if (status === 400) return 'That change was not accepted. Ask Neo again.';
  return 'Could not apply the change. Try again.';
}
